import { Button, Tooltip } from 'antd';
import type { FormInstance } from 'antd';
import { CaseSidebarItemId } from '@components/CaseSidebar/types';
import { FormValidationContext } from '@hooks/useBtlFormValidation';
import { useContext } from 'react';
import { useIntl } from 'react-intl';

interface OoCaseFormFooterProps {
  previousSection?: CaseSidebarItemId;
  nextSection?: CaseSidebarItemId;
  previousLabel?: string;
  nextLabel?: string;
  onNavigate: (itemId: CaseSidebarItemId) => void;
  form?: FormInstance;
  isFormEditable?: boolean;
  showSkipButton?: boolean;
  submitLabel?: string;
  onSubmit?: () => void;
  isSubmitDisabled?: boolean;
  submitDisabledReason?: string;
}

export function OoCaseFormFooter({
  previousSection,
  nextSection,
  previousLabel,
  nextLabel,
  onNavigate,
  form,
  isFormEditable,
  showSkipButton,
  submitLabel,
  onSubmit,
  isSubmitDisabled,
  submitDisabledReason,
}: OoCaseFormFooterProps) {
  const { formatMessage } = useIntl();
  const formValidationContext = useContext(FormValidationContext);
  const showForwardAction = Boolean(form) || Boolean(nextSection) || Boolean(submitLabel);

  if (!previousSection && !showForwardAction) {
    return null;
  }

  const submitButtonLabel =
    submitLabel ?? nextLabel ?? (form && !nextSection ? formatMessage({ id: 'common.saveContinue' }) : 'Next');

  const isDisabled =
    Boolean(isSubmitDisabled) || (!onSubmit && !form && formValidationContext ? !formValidationContext.isValid : false);

  const submitButton = (
    <Button
      size='large'
      type='primary'
      key='next'
      htmlType='button'
      loading={formValidationContext?.isSubmitting}
      disabled={isDisabled}
      onClick={() => {
        if (onSubmit) {
          onSubmit();
          return;
        }

        if (form) {
          form.submit();
          return;
        }

        formValidationContext?.onSubmitForm();
      }}
    >
      {submitButtonLabel}
    </Button>
  );

  return (
    <div className='absolute bottom-0 right-0 z-10 mt-4 flex w-full justify-between gap-8 rounded-lg border border-solid border-white bg-gray-500/10 p-2 backdrop-blur-md'>
      <span className='flex gap-2'>
        {previousSection && (
          <Button size='large' key='back' onClick={() => onNavigate(previousSection)}>
            {previousLabel ?? 'Back'}
          </Button>
        )}
      </span>
      {isFormEditable && showForwardAction && (
        <span className='flex gap-2'>
          {showSkipButton && nextSection && (
            <Button size='large' onClick={() => onNavigate(nextSection)}>
              Skip
            </Button>
          )}
          {isSubmitDisabled && submitDisabledReason ? (
            <Tooltip title={submitDisabledReason} placement='topLeft'>
              <span className='inline-block'>{submitButton}</span>
            </Tooltip>
          ) : (
            submitButton
          )}
        </span>
      )}
      {!isFormEditable && nextSection && (
        <span className='flex gap-2'>
          <Button size='large' type='primary' key='next' onClick={() => onNavigate(nextSection)}>
            {nextLabel ?? 'Next'}
          </Button>
        </span>
      )}
    </div>
  );
}
